/**
 * Screen guides — a screen's full explanation in five fixed parts, and the guided tour that walks
 * the same screen (ADR 9044).
 *
 * ## Why five fixed parts rather than a paragraph
 *
 * `docSectionKey` (bata-ui#132 step 6) gave each screen one paragraph, and five of fifty-two
 * routes ever declared one. A free paragraph has no shape a reviewer can check: whether it says
 * what the actions write, or what an empty table means, is left to whoever wrote it. The five
 * parts are the questions a planner actually brings to a screen, so a guide that skips one is
 * visibly incomplete:
 *
 *   purpose   what question the screen answers, and when to open it
 *   reading   how to read it — each panel, figure and column, in screen order
 *   actions   what can be done here, who may, what it writes, whether it can be undone
 *   states    what each empty, awaiting, not-published or refused state means
 *   source    where the figures come from, as at when, and what the address selects, in business
 *             words (no endpoint, path, ADR number or identifier: owner ruling 2026-10-19)
 *
 * What the manifest already knows — where the screen drills to, what reaches it, what it exports
 * — is NOT written here: Documentation derives it from the manifest, so it cannot drift.
 *
 * ## A chapter opts in as a whole
 *
 * `FeatureManifest.guides` is keyed by route `path`, exactly as the manifest declares it. Once a
 * chapter declares the table, `tools/doc-sections.mjs` requires an entry for EVERY route of that
 * chapter — a guide, or an exemption with its reason — so a screen added later cannot ship
 * without one. Keys are written out in full rather than built from the path, so `i18n:coverage`
 * sees each of them and the census can read them.
 */

import type { TourStep } from '../planning/tour';

export type { TourStep } from '../planning/tour';

export interface ScreenGuide {
  readonly purposeKey: string;
  readonly readingKey: string;
  readonly actionsKey: string;
  readonly statesKey: string;
  readonly sourceKey: string;
  /** The steps of the guided tour, in screen order. Read-only: no step presses anything. */
  readonly tour: readonly TourStep[];
}

/** A route that has no guide, and says why — a redirect, or a screen not offered. */
export interface GuideExemption {
  readonly exempt: string;
}

export type GuideEntry = ScreenGuide | GuideExemption;

/** A chapter's guides, keyed by route `path` as its manifest declares it. */
export type GuideTable = Readonly<Record<string, GuideEntry>>;

export function isScreenGuide(entry: GuideEntry | undefined): entry is ScreenGuide {
  return entry !== undefined && !('exempt' in entry);
}

/** The five parts, in the order Documentation draws them. */
export const GUIDE_PARTS = ['purposeKey', 'readingKey', 'actionsKey', 'statesKey', 'sourceKey'] as const;
export type GuidePart = (typeof GUIDE_PARTS)[number];
